import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import { Layout, SEO, Section, Container, PreviewCard, HtmlToElement } from "@/components";
import { DATOCMS_API_TOKEN } from "@/config";
import { metaTagsFragment, responsiveImageFragment } from "@/modules";
import { Subscription, IPartnership } from "@/types";
import { createHref } from "@/utils";
import { HorizontalRule } from "@narrative-software/narrative-web-ui";

interface IPartnershipHolder {
  partnerships: IPartnership[];
  page: {
    id: string;
    title: string;
    slug: string;
    subheading: string;
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<IPartnershipHolder>;
};

const PartnershipHolder: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const {
    partnerships,
    page: { seo, title, subheading },
  } = data;

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container>
          <div className="flex flex-col items-center mb-12 text-center">
            <h1 className="text-h2 font-semibold leading-tight lg:text-h1">{title}</h1>
            <HorizontalRule topMargin bottomMargin={!!subheading} />
            {subheading && <HtmlToElement className="max-w-200 lg:text-lg">{subheading}</HtmlToElement>}
          </div>

          {partnerships.length ? (
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {partnerships.map(({ id, summary, ...rest }) => (
                <PreviewCard {...rest} key={id} href={createHref(rest)} description={summary} />
              ))}
            </div>
          ) : (
            <p className="text-center">There are currently no partnership pages.</p>
          )}
        </Container>
      </Section>
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pagePartnershipHolder {
          title
          subheading
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }

        partnerships: allPagePartnerships(orderBy: position_ASC) {
          id
          title
          slug
          summary
          parentPage {
            slug
          }
          image {
            responsiveImage(imgixParams: { auto: format, fit: crop, w: 780, h: 446 }) {
              ...responsiveImageFragment
            }
          }
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IPartnershipHolder>(graphqlRequest);

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PartnershipHolder;
